/**
 * Shared vocabulary for client and server. See docs/IMPLEMENTATION_PLAN.md §6.
 *
 * Nothing in this file may describe a player's dice to anyone but their owner.
 * `PublicPlayer` carries `diceCount`, never faces; the only type that carries
 * faces is `ClientView['you']` and `Reveal['allDice']`, and both are produced
 * exclusively by `toClientView` in src/worker/view.ts.
 */

export type Face = 1 | 2 | 3 | 4 | 5 | 6;

export const FACES: readonly Face[] = [1, 2, 3, 4, 5, 6];

/** A bid stripped of provenance — enough to reason about legality. */
export type BidValue = {
  quantity: number;
  face: Face;
};

/** A bid as actually placed at a table. */
export type Bid = BidValue & {
  playerId: string;
  /** epoch ms */
  at: number;
};

export type Phase = 'lobby' | 'rolling' | 'bidding' | 'revealing' | 'gameOver';

export type PublicPlayer = {
  id: string;
  name: string;
  /** 0..5, fixed for the life of the table */
  seat: number;
  /** COUNT ONLY — never the faces */
  diceCount: number;
  connected: boolean;
  eliminated: boolean;
  /**
   * Beyond §6's list. The lobby is unusable without it — nobody can tell who
   * they are waiting for. Public by nature, and carries no hidden information.
   */
  ready: boolean;
};

export type Reveal = {
  bid: Bid;
  challengerId: string;
  actual: number;
  /**
   * The primary loser — for a challenge, the single player who pays. Kept
   * because §6 names it, but prefer `loserIds`: a successful `exact` call costs
   * *every other player* a die, which one id cannot express.
   */
  loserId: string;
  /** Everyone who lost a die on this reveal. One entry for a challenge. */
  loserIds: string[];
  /** Only populated in a `revealing` or `gameOver` phase. */
  allDice: Record<string, Face[]>;
};

export type GameConfig = {
  turnSeconds: number;
  /** calza — see §3.5. Off in v1. */
  allowExact: boolean;
  /** see §3.5. Off in v1. */
  palifico: boolean;
};

export type PublicState = {
  tableId: string;
  phase: Phase;
  players: PublicPlayer[];
  activePlayerId: string | null;
  currentBid: Bid | null;
  /** current round only */
  bidHistory: Bid[];
  roundNumber: number;
  /** epoch ms */
  turnDeadline: number | null;
  lastReveal: Reveal | null;
  winnerId: string | null;
  config: GameConfig;
  /**
   * Beyond §6's list. The client needs it to decide whether to offer the Start
   * button (§7 makes `start` host-only). Public by nature.
   */
  hostId: string | null;
};

export type ClientView = PublicState & {
  you: { id: string; dice: Face[] };
};

export const DEFAULT_CONFIG: GameConfig = {
  turnSeconds: 60,
  allowExact: false,
  palifico: false,
};

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 6;
export const STARTING_DICE = 5;
export const MAX_NAME_LENGTH = 24;
